import React, { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { fetchUserAttributes, getCurrentUser } from "@aws-amplify/auth";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import {
  createUserPayments,
  updateUserPayments,
  updateCoupon,
} from "../graphql/mutations.js";
import {
  getCoupon,
  listCoupons,
  listUserPayments,
  listUserLinks,
} from "../graphql/queries.js";
import "./WholisticHealthProgram.css";

const PAYPAL_CLIENT_ID =
  "AbTmKuryR63tH6ernrjPWq4bD-bJCwzObx8HPzxH-vDjeXNF8KEZwZC4_kokki9kiwU4UQRTGjcBzmDk";
const BASE_PRICE = 10;
const FREE_TRIAL_CODE = "FREETRIAL";
const FREE_TRIAL_DAYS = 14;

async function resolveCouponByCode(client, inputCode) {
  const code = String(inputCode || "").trim();
  if (!code) return null;

  const candidateCodes = Array.from(
    new Set([code, code.toUpperCase(), code.toLowerCase()]),
  );

  for (const candidate of candidateCodes) {
    const res = await client.graphql({
      query: getCoupon.replaceAll("__typename", ""),
      variables: { id: candidate },
    });
    const coupon = res?.data?.getCoupon || res?.data?.GetCoupon || null;
    if (coupon) return coupon;
  }

  const listRes = await client.graphql({
    query: listCoupons,
    variables: { limit: 100 },
  });
  const coupons = listRes?.data?.listCoupons?.items || [];
  const normalized = code.toLowerCase();
  return (
    coupons.find(
      (item) =>
        String(item?.id || "")
          .trim()
          .toLowerCase() === normalized,
    ) || null
  );
}

export default function WholisticHealthProgram({ setCurrentView }) {
  const client = generateClient();
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [userId, setUserId] = useState(null);

  const [coupon, setCoupon] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [validating, setValidating] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    async function checkAccess() {
      try {
        const currentUser = await getCurrentUser().catch(() => null);
        const attrs = await fetchUserAttributes().catch(() => null);
        const uid = attrs?.sub || currentUser?.username || null;
        if (!uid) {
          setCheckingAccess(false);
          return;
        }
        setUserId(uid);

        const res = await client.graphql({
          query: listUserPayments,
          variables: {
            filter: { or: [{ owner: { eq: uid } }, { userId: { eq: uid } }] },
            limit: 50,
          },
        });
        const paymentRecords = res?.data?.listUserPayments?.items || [];
        const now = new Date();
        const validPayments = paymentRecords.filter(
          (pr) =>
            new Date(pr.paidUntil) > now &&
            (pr.paymentFor === "WholisticHealthProgram" ||
              pr.paymentFor === "Individual"),
        );
        if (validPayments.length > 0) {
          setHasAccess(true);
        } else {
          // Check if user is linked to a professional who has paid
          try {
            const userEmail =
              attrs?.email || currentUser?.signInDetails?.loginId;
            const normalizedUserEmail = userEmail?.toLowerCase();
            if (normalizedUserEmail) {
              const linksRes = await client.graphql({
                query: listUserLinks,
                variables: {
                  filter: {
                    or: [
                      { clientEmail: { eq: normalizedUserEmail } },
                      { clientEmail: { eq: userEmail } },
                    ],
                  },
                  limit: 50,
                },
              });

              let links = linksRes?.data?.listUserLinks?.items || [];
              if (links.length === 0) {
                const allLinksRes = await client.graphql({
                  query: listUserLinks,
                  variables: { limit: 500 },
                });
                const allLinks = allLinksRes?.data?.listUserLinks?.items || [];
                links = allLinks.filter(
                  (link) =>
                    (link?.clientEmail || "").toLowerCase() ===
                    normalizedUserEmail,
                );
              }
              if (links.length > 0) {
                const activeLinks = links.filter(
                  (link) =>
                    link?.clientViewable !== false &&
                    !link?.unlinkedAt &&
                    (link?.professionalId || link?.professionalEmail),
                );

                if (activeLinks.length > 0) {
                  console.log(
                    "✅ WHP access granted via active professional connection",
                  );
                  setHasAccess(true);
                  return;
                }

                // Check if any linked professional has active payments
                for (const link of links) {
                  const proId = link.professionalId;
                  if (!proId) continue;

                  // IMPORTANT (auth): clients/individuals are not authorized to read
                  // other users' OrganizationCPs records. Prefer org context already
                  // present on the link instead.
                  const orgIdFromLink =
                    link?.organizationId ||
                    attrs?.["custom:organizationId"] ||
                    null;

                  // Use org ID if the link is org-scoped; otherwise use professional ID
                  const paymentUserId = orgIdFromLink || proId;

                  const proPaymentsRes = await client.graphql({
                    query: listUserPayments,
                    variables: {
                      filter: {
                        and: [
                          { userId: { eq: paymentUserId } },
                          { paidUntil: { gt: now.toISOString() } },
                          {
                            or: [
                              { paymentFor: { eq: "professional" } },
                              { paymentFor: { eq: "Professional" } },
                              { paymentFor: { eq: "organization" } },
                            ],
                          },
                        ],
                      },
                      limit: 50,
                    },
                  });

                  const proPayments =
                    proPaymentsRes?.data?.listUserPayments?.items || [];
                  const hasActiveProPayment = proPayments.some(
                    (p) => (p.clientsProjected || 0) > 0,
                  );

                  if (hasActiveProPayment) {
                    console.log(
                      "✅ WHP access granted via professional connection",
                    );
                    setHasAccess(true);
                    break;
                  }
                }
              }
            }
          } catch (linkErr) {
            console.warn("Error checking professional links for WHP:", linkErr);
          }
        }
      } catch (err) {
        console.warn("WHP access check failed:", err);
      } finally {
        setCheckingAccess(false);
      }
    }

    checkAccess();
  }, [client]);

  const calculateFinalPrice = () => {
    if (!couponData) return BASE_PRICE.toFixed(2);
    let final = BASE_PRICE;
    if (couponData.type === "amount") {
      final = Math.max(0.01, BASE_PRICE - couponData.value);
    } else if (couponData.type === "percent") {
      final = Math.max(
        0.01,
        BASE_PRICE - BASE_PRICE * (couponData.value / 100),
      );
    }
    return parseFloat(final.toFixed(2));
  };

  const handleValidateCoupon = async () => {
    if (couponData) {
      setCouponError("You can only use one coupon at a time.");
      return;
    }
    const code = coupon.trim();
    if (!code) {
      setCouponError("Please enter a coupon code.");
      return;
    }
    setValidating(true);
    setCouponError("");
    try {
      const currentUser = await getCurrentUser().catch(() => null);
      if (!currentUser) {
        setValidating(false);
        setCouponError("You must sign in to apply coupons.");
        return;
      }
      const data = await resolveCouponByCode(client, code);
      if (!data) throw new Error("Invalid or expired coupon");
      setCouponData(data);

      let finalPrice = BASE_PRICE;
      if (data.type === "amount")
        finalPrice = Math.max(0.01, BASE_PRICE - data.value);
      else if (data.type === "percent")
        finalPrice = Math.max(
          0.01,
          BASE_PRICE - BASE_PRICE * (data.value / 100),
        );
      finalPrice = parseFloat(finalPrice.toFixed(2));

      if (finalPrice <= 0.01) {
        const isFreeTrialCoupon = code === FREE_TRIAL_CODE;

        if (isFreeTrialCoupon) {
          const priorUseRes = await client.graphql({
            query: listUserPayments,
            variables: {
              filter: {
                and: [
                  {
                    or: [{ userId: { eq: userId } }, { owner: { eq: userId } }],
                  },
                  { couponCode: { eq: code } },
                ],
              },
              limit: 10,
            },
          });

          if (priorUseRes?.data?.listUserPayments?.items?.length) {
            throw new Error(
              "This free trial coupon has already been used for this account.",
            );
          }
        }

        if (!userId) throw new Error("User not authenticated");
        const paidUntilDate = new Date();
        paidUntilDate.setDate(
          paidUntilDate.getDate() + (isFreeTrialCoupon ? FREE_TRIAL_DAYS : 365),
        );
        // create or update payment record
        try {
          const checkRes = await client.graphql({
            query: listUserPayments,
            variables: {
              filter: {
                or: [{ owner: { eq: userId } }, { userId: { eq: userId } }],
              },
              limit: 50,
            },
          });
          const paymentRecords = checkRes?.data?.listUserPayments?.items || [];
          const existingPayment = paymentRecords.length
            ? paymentRecords[0]
            : null;
          if (existingPayment) {
            await client.graphql({
              query: updateUserPayments,
              variables: {
                input: {
                  id: existingPayment.id,
                  userId,
                  paidUntil: paidUntilDate.toISOString(),
                  paymentFor: "WholisticHealthProgram",
                },
              },
            });
          } else {
            await client.graphql({
              query: createUserPayments,
              variables: {
                input: {
                  userId,
                  paidUntil: paidUntilDate.toISOString(),
                  paymentFor: "WholisticHealthProgram",
                },
              },
            });
          }
          if (data) {
            await client.graphql({
              query: updateCoupon,
              variables: {
                input: {
                  id: data.id,
                  timesUsed: (data.timesUsed || 0) + 1,
                  _version: data._version,
                },
              },
            });
          }
          setHasAccess(true);
          setSuccessMsg(
            isFreeTrialCoupon
              ? `Coupon applied — access granted until ${paidUntilDate.toLocaleDateString()} (${FREE_TRIAL_DAYS} days).`
              : "Coupon applied — access granted for 1 year.",
          );
        } catch (err) {
          console.error("Error applying free coupon:", err);
          setCouponError(
            err?.message || "Failed to apply free coupon. Please try again.",
          );
          setSuccessMsg("");
        } finally {
          setValidating(false);
        }
        return;
      }

      setCouponError("");
      setValidating(false);
      setSuccessMsg(`Coupon applied! New price: $${finalPrice}`);
    } catch (err) {
      console.error("Error validating coupon:", err);
      setCouponData(null);
      setCouponError("Invalid coupon code.");
      setValidating(false);
    }
  };

  const handlePayPalApprove = async (orderData) => {
    try {
      // successful capture recorded by PayPalButtons; now persist access for 1 year
      if (!userId) {
        const u = await getCurrentUser().catch(() => null);
        const uid = u?.attributes?.sub || u?.username || null;
        if (!uid) throw new Error("User not authenticated");
        setUserId(uid);
      }
      const paidUntilDate = new Date();
      paidUntilDate.setFullYear(paidUntilDate.getFullYear() + 1);

      const checkRes = await client.graphql({
        query: listUserPayments,
        variables: {
          filter: {
            or: [{ owner: { eq: userId } }, { userId: { eq: userId } }],
          },
          limit: 50,
        },
      });
      const paymentRecords = checkRes?.data?.listUserPayments?.items || [];
      const existingPayment = paymentRecords.length ? paymentRecords[0] : null;
      if (existingPayment) {
        await client.graphql({
          query: updateUserPayments,
          variables: {
            input: {
              id: existingPayment.id,
              userId,
              paidUntil: paidUntilDate.toISOString(),
              paymentFor: "WholisticHealthProgram",
            },
          },
        });
      } else {
        await client.graphql({
          query: createUserPayments,
          variables: {
            input: {
              userId,
              paidUntil: paidUntilDate.toISOString(),
              paymentFor: "WholisticHealthProgram",
            },
          },
        });
      }

      setHasAccess(true);
      setSuccessMsg("Payment received — access granted for 1 year.");
    } catch (err) {
      console.error("Error finalizing payment:", err);
      setCouponError("Failed to finalize payment. Please try again.");
    }
  };

  if (checkingAccess)
    return (
      <div
        className="whp-container"
        style={{
          width: 350,
          minWidth: 300,
          backgroundColor: "white",
          padding: 5,
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          color: "#333",
        }}
      >
        Checking access…
      </div>
    );

  if (!hasAccess) {
    const final = calculateFinalPrice();
    return (
      <div
        className="whp-container"
        style={{
          width: 350,
          minWidth: 300,
          backgroundColor: "white",
          padding: 5,
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          color: "#333",
        }}
      >
        <img
          src={(process.env.PUBLIC_URL || "") + "/Icon-WHP-Blue.png"}
          alt="Logo"
          style={{
            width: "100%",
            maxWidth: 200,
            height: "auto",
            display: "block",
            margin: "20px auto 30px auto",
          }}
        />
        <h1 className="whp-title">Wholistic Health Program ©</h1>
        <p className="whp-sub">
          This provides easy access to the program videos,
          journaling/notetaking, as well as access to create a health plan and
          track progress for one (1) year.
        </p>

        <div className="whp-pay">
          <div style={{ marginBottom: 12 }}>
            <strong>Price:</strong> ${final}
          </div>
          <div className="whp-coupon">
            <input
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              placeholder="Coupon code"
            />
            <button onClick={handleValidateCoupon} disabled={validating}>
              Apply
            </button>
            {couponError && <div className="whp-error">{couponError}</div>}
            {successMsg && <div className="whp-success">{successMsg}</div>}
          </div>

          <div style={{ marginTop: 12 }}>
            <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID }}>
              <PayPalButtons
                style={{ layout: "vertical" }}
                createOrder={(data, actions) =>
                  actions.order.create({
                    purchase_units: [{ amount: { value: String(final) } }],
                  })
                }
                onApprove={async (data, actions) => {
                  const details = await actions.order.capture();
                  await handlePayPalApprove(details);
                }}
              />
            </PayPalScriptProvider>
          </div>
        </div>
      </div>
    );
  }

  const cards = [
    "Introduction",
    ...Array.from({ length: 10 }, (_, i) => `Exercise ${i + 1}`),
    "Conclusion",
  ];

  const cardDescriptions = [
    "Get started with understanding your whole self and take meaningful steps toward clarity, confidence, and connection across every dimension of health.",
    "Review principles for naturally detoxing and set a solid foundation for why and how to fast during the Wholistic Health Program ©.",
    "Identify and address unhealthy habits and review how forgiveness (of ourselves and others) unlocks health.",
    "Choose and begin strengthening healthy habits while documenting your vision for your life and establishing your identity and purpose.",
    "Identify and address traumatic experiences in a healthy way and start the process of breaking spiritual trauma and oppression.",
    "Practice using granular emotions to express thoughts and feelings and learn how negative spiritual forces use emotions and how you can maintain and regain control.",
    "Identify unhealthy thinking patterns and begin correcting destructive thoughts, including overcoming fear.",
    "See practical ways words shape your life and how to use words to reshape your future and identity.",
    "Practice setting healthy boundaries and clear expectations and how to use spiritual weapons with challenging people in your life.",
    "Start detoxing your environment, including your home and choices of entertainment and learn how to continually guard your heart and mind.",
    "Reflect on your relationships, specifically your inner circle and prepare to carefully remove negative relationships in your life.",
    "Solidify your next steps for continued growth and commitment to your health journey.",
  ];

  const cardImages = [
    "Introduction - Together.png",
    "Exercise 1 - Plant Based.jpg",
    "Exercise 2 - Counseling.jpg",
    "Exercise 3 - Man Riding Bike.jpg",
    "Exercise 4 - Health Coaching.jpg",
    "Exercise 5 - Man Reflecting.jpg",
    "Exercise 6 - Man Hiking.jpg",
    "Exercise 7 - Woman Looking in Mirror.jpg",
    "Exercise 8 - Older Black Man.jpg",
    "Exercise 9 - Peaceful Home.jpg",
    "Exercise 10 - Group of Friends.jpg",
    "Conclusion - Woman Fulfilled.png",
  ];

  return (
    <div
      className="whp-container"
      style={{
        width: 350,
        minWidth: 300,
        backgroundColor: "white",
        padding: 5,
        borderRadius: 12,
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        color: "#333",
      }}
    >
      <img
        src={(process.env.PUBLIC_URL || "") + "/Icon-WHP-Blue.png"}
        alt="Logo"
        style={{
          width: "100%",
          maxWidth: 200,
          height: "auto",
          display: "block",
          margin: "20px auto 30px auto",
        }}
      />
      <h1 className="whp-title">Wholistic Health Program ©</h1>
      <p className="whp-sub" style={{ padding: "0 20px" }}>
        Welcome — You have access to this feature as well as "Create Plan" and
        "Track Progress" for 1 year. This resource offers convenient access to
        the Wholistic Health Program © videos for each exercise and a journal /
        note-taking section. The program will be more effective{" "}
        <a
          href="https://a.co/d/0fajMzGH"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#0067B3", fontWeight: "600" }}
        >
          with the workbook
        </a>
        . The spiritual health component is optional.
      </p>
      <div className="whp-grid">
        {cards.map((t, idx) => (
          <div
            className="whp-card"
            key={idx}
            onClick={() => {
              // idx 0: Introduction
              if (idx === 0)
                return setCurrentView("WholisticHealthIntroduction");
              // idx 1-10: Exercises 1..10
              if (idx >= 1 && idx <= 10)
                return setCurrentView(`WholisticHealthExercise${idx}`);
              // last: Conclusion
              if (idx === cards.length - 1)
                return setCurrentView("WholisticHealthConclusion");
            }}
          >
            {cardImages[idx] && (
              <img
                className="whp-card-img"
                src={`/images/${encodeURIComponent(cardImages[idx])}`}
                alt={t}
                onError={(e) => {
                  // Fallback: images might be placed directly in public/
                  if (!e.target.dataset.fallback) {
                    e.target.dataset.fallback = "1";
                    e.target.src = `/${encodeURIComponent(cardImages[idx])}`;
                  }
                }}
              />
            )}
            <div className="whp-card-title">{t}</div>
            <div className="whp-card-text">
              {cardDescriptions[idx] || `Content for ${t}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
